import { formatMoney } from '@lawbee/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CalendarClock, ChevronRight, ClipboardList, LockKeyhole, MapPin, Phone } from 'lucide-react';
import type { ReactNode } from 'react';
import { api } from '../../lib/api';
import { useParam } from '../../lib/hooks';
import { PublicFrame } from '../../shell/RootPages';
import { ConfidentialityNotice, publicQueryState } from './shared';

export type PublicFirm = {
  name: string;
  slug: string;
  phone?: string | null;
  address?: string | null;
  confidentialityNotice: string;
  forms: { slug: string; name: string }[];
  bookingTypes: { slug: string; name: string; kind: string; durationMin: number; priceCents: number; description: string | null }[];
  portalEnabled: boolean;
};

export function useFirmQuery(slug: string, opts: { retry?: boolean } = {}) {
  return useQuery({
    queryKey: ['public', 'firm', slug],
    queryFn: () => api.get<PublicFirm>(`/api/public/firm/${encodeURIComponent(slug)}`),
    retry: opts.retry ?? false,
    enabled: !!slug,
  });
}

function Section({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold [&_svg]:size-4 [&_svg]:text-muted-foreground">
        {icon}
        {title}
      </h2>
      {children}
    </section>
  );
}

const rowClass =
  'flex items-center gap-3 rounded-xl bg-card px-4 py-3.5 shadow-card transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring/25';

export function FirmPage() {
  const slug = useParam('slug');
  const q = useFirmQuery(slug);
  const state = publicQueryState(q, { loadingLabel: 'Loading' });
  if (state) return state;
  const firm = q.data!;
  const nothing = firm.forms.length === 0 && firm.bookingTypes.length === 0 && !firm.portalEnabled;

  return (
    <PublicFrame firmName={firm.name}>
      <div className="space-y-8">
        <header className="space-y-2">
          <h1 className="text-[30px] font-semibold leading-tight tracking-[-0.03em]">{firm.name}</h1>
          {firm.address || firm.phone ? (
            <div className="flex flex-col gap-1 text-sm text-muted-foreground sm:flex-row sm:gap-5">
              {firm.address ? (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="size-4" aria-hidden /> {firm.address}
                </span>
              ) : null}
              {firm.phone ? (
                <a href={`tel:${firm.phone}`} className="figures inline-flex items-center gap-1.5 hover:text-foreground">
                  <Phone className="size-4" aria-hidden /> {firm.phone}
                </a>
              ) : null}
            </div>
          ) : null}
        </header>

        {nothing ? <p className="text-[15px] text-muted-foreground">Please contact the firm directly.</p> : null}

        {firm.forms.length > 0 ? (
          <Section icon={<ClipboardList />} title="Tell us about your matter">
            <ul className="space-y-2">
              {firm.forms.map((f) => (
                <li key={f.slug}>
                  <Link to="/intake/$slug/$formSlug" params={{ slug: firm.slug, formSlug: f.slug }} className={rowClass}>
                    <span className="flex-1 text-[15px] font-medium">{f.name}</span>
                    <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {firm.bookingTypes.length > 0 ? (
          <Section icon={<CalendarClock />} title="Book a consultation">
            <ul className="space-y-2">
              {firm.bookingTypes.map((t) => (
                <li key={t.slug}>
                  <Link to="/book/$slug/$typeSlug" params={{ slug: firm.slug, typeSlug: t.slug }} className={rowClass}>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[15px] font-medium">{t.name}</span>
                      {t.description ? <span className="mt-0.5 block text-sm text-muted-foreground">{t.description}</span> : null}
                      <span className="mt-1 block text-[13px] text-muted-foreground">
                        <span className="figures">{t.durationMin} min</span>
                        <span aria-hidden> · </span>
                        <span className="figures">{t.priceCents > 0 ? formatMoney(t.priceCents) : 'Free'}</span>
                      </span>
                    </span>
                    <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {firm.portalEnabled ? (
          <Section icon={<LockKeyhole />} title="Existing clients">
            <Link to="/portal/$slug" params={{ slug: firm.slug }} className={rowClass}>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-medium">Sign in to the client portal</span>
                <span className="mt-0.5 block text-sm text-muted-foreground">See your matter status, documents and invoices.</span>
              </span>
              <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
            </Link>
          </Section>
        ) : null}

        <ConfidentialityNotice notice={firm.confidentialityNotice} />
      </div>
    </PublicFrame>
  );
}
